<template>
  <b-card
    :id="`tooltip_products_error_${collection._id}`"
    class="mb-2 collection-card"
    :class="{'no-products-collection border-danger': isErrorInProducts}"
    no-body
  >
    <b-link
      id="collection0"
      :to="{ name: 'collection/products', params: { collectionId: collection._id } }"
    >
      <b-img
        :src="collection.logo || getDefaultProductImage(300, 300)"
        class="collection-logo"
        @error="onImageError"
      />
      <b-card-body class="p-75 position-relative">
        <b-dropdown
          v-if="isCollectionListPage"
          no-caret
          class="collection-actions-dropdown"
          menu-class="z-1100"
          variant="none"
          toggle-class="p-50"
          right
          @click.stop.prevent
        >
          <template #button-content>
            <div class="collection-actions-toggle">
              <feather-icon
                icon="MoreVerticalIcon"
                size="20"
              />
            </div>
          </template>
          <b-dropdown-item
            v-if="isEntityAdmin"
            :to="{ name: 'collection/edit', params: { id: collection._id } }"
            @click="beforeEdit(collection)"
          >
            Edit Collection
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin"
            @click="showModal(collection)"
          >
            Update Availability
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin"
            @click="showCollectionWarningModal(collection)"
          >
            Delete Collection
          </b-dropdown-item>
          <b-dropdown-item @click="onHandleDownloadCollection">
            Download Collection
          </b-dropdown-item>
          <b-dropdown-item @click="copyCollectionLink(collection)">
            Copy Collection’s Link
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin"
            @click="onUpdateCollectionStatus(collection.status)"
          >
            <span :class="isCollectionLive(collection.status) ?'text-danger' :'text-success'"> Change to {{ isCollectionLive(collection.status) ? COLLECTION_STATUS.EXPIRED.LABEL : COLLECTION_STATUS.LIVE.LABEL }}? </span>
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin"
            @click="$refs.collectionModals.getCollectionPriceBook(true)"
          >
            Price Books
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin"
            @click="$refs.collectionModals.openCurrencyBook()"
          >
            Currency Books
          </b-dropdown-item>
          <b-dropdown-item
            v-if="isEntityAdmin && hasTransactionCurrencyFeature"
            @click="$refs.collectionModals.openSegmentationBook()"
          >
            Segmentation Books
          </b-dropdown-item>
          <!-- <b-dropdown-item
            v-if="isEnableSync"
            @click="syncWithShopify(collection)"
          >
            Sync with Shopify
          </b-dropdown-item> -->
        </b-dropdown>
        <div class="title d-flex align-items-center justify-content-between mr-25">
          <span> {{ collection.name }} </span>
          <!-- Retailers have always had a Download button here. KP-3919
               restyles it as an icon; with the flag off it stays the button
               it was, and the modal behind it falls back the same way. -->
          <template v-if="isRetailer">
            <download-icon-button
              v-if="isBrandCatalogueEnabled"
              :aria-label="`Download ${collection.name}`"
              @click="$bvModal.show(`download_catalog_${collection._id}`)"
            />
            <b-button
              v-else
              variant="outline-none"
              class="download-btn"
              @click.stop.prevent="$bvModal.show(`download_catalog_${collection._id}`)"
            >
              Download
            </b-button>
          </template>
        </div>

        <div
          v-if="isCollectionListPage"
          class="mt-50"
        >
          {{ collection.productsCount || 0 }} Product{{ collection.productsCount > 1 ? 's' : '' }}
        </div>
        <div
          v-if="collection.type && !isPreviousCollection"
          class="mt-50 collection-type"
          :class="parseCollectionTypeClass(collection.type)"
        >
          {{ getCollectionTypeText(collection.type) }}
        </div>
        <div
          v-if="isPreviousCollection"
          class="mt-50 collection-type closed-bg"
        >
          Deadline Closed
        </div>
        <div class="mt-50">
          {{ parseCollectionDate(collection.endDate) }}
        </div>
        <div
          v-if="isCollectionListPage"
          class="mt-50"
        >
          Updated {{ formattedDate(collection.updatedAt) }}
        </div>
        <div
          v-if="collection.expectedDeliveryWindow && collection.expectedDeliveryWindow.fromDate && collection.expectedDeliveryWindow.toDate"
          class="mt-50"
        >
          Delivery window: {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.fromDate) }} - {{ getMonthAndYearFromDate(collection.expectedDeliveryWindow.toDate) }}
        </div>
        <div
          class="mt-50"
        >
          <span :class="readMore ? '':'read-more-text'"> Description: {{ getDescription(collection.description) }} </span>
          <span
            v-if="collection.description.length > DESCRIPTION_MAX_SHOWING_LENGTH"
            class="read-more"
            @click.stop.prevent="readMore = !readMore"
          > Read {{ readMore ? 'Less' : 'More' }} </span>
        </div>
        <!-- Disabling this temporarily -->
        <b-button
          v-if="false"
          class="w-100"
          variant="custom-primary"
        >
          Download LookBook
        </b-button>
      </b-card-body>
    </b-link>
    <dowload-catalog :collection="collection" />
    <collection-modals
      ref="collectionModals"
      :fetch-collections="fetchCollections"
      :collection="collection"
    />
    <b-tooltip
      v-if="isErrorInProducts"
      class="font-Inter"
      :target="`tooltip_products_error_${collection._id}`"
      placement="bottom"
      triggers="hover"
      variant="danger"
    >
      {{ PRODUCTS_ERROR_MESSAGE }}
    </b-tooltip>
    <b-modal
      id="modal-select"
      ref="collectionWarning"
      size="lg"
      modal-class="modal-primary custom-model"
      centered
      hide-header-close
      hide-header
      title="Collection Warning"
      hide-footer
    >
      <div>
        <div class="pb-50 border-bottom">
          <span class="custom-modal-heading">Collection Warning </span>
          <feather-icon
            class="float-right close-x-icon"
            icon="XIcon"
            size="24"
            @click="closeCollectionWarningModal"
          />
        </div>
        <p
          class="mt-1 text-center product-message"
          v-html="collectionRemoveMessage"
        />
      </div>
      <div>
        <div class="text-center mt-1">
          <b-button
            v-show="canDeleteCollection"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            type="reset"
            class="mt-2 mr-2 px-sm-3 font-weight-bolder"
            variant="outline-info"
            @click="closeCollectionWarningModal"
          >
            Cancel
          </b-button>
          <b-button
            v-show="canDeleteCollection"
            type="submit"
            variant="info"
            class="mt-2 px-sm-3 font-weight-bolder"
            :disabled="isSaving"
            @click="onRemoveCollection"
          >
            {{
              isSaving ? 'Removing Collection...' : 'Yes, remove the collection'
            }}
          </b-button>
          <b-button
            v-show="!canDeleteCollection"
            type="reset"
            variant="info"
            class="mt-2 px-sm-3 font-weight-bolder"
            :disabled="isSaving"
            @click="closeCollectionWarningModal"
          >
            Ok! I understand
          </b-button>
        </div>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
 BCard, BImg, BLink, BCardBody, BButton, BDropdown, BDropdownItem, BTooltip,
} from 'bootstrap-vue'
import DownloadIconButton from '@/components/collection/DownloadIconButton.vue'
import { useBrandCatalogueFlag } from '@/composables/useBrandCatalogueFlag'
import { SYNC_COLLECTION_WITH_SHOPIFY } from '@/store/modules/collection.module'
import store from '@/store'
import { apiToastSuccess } from '@/@core/utils/toast'
import { useCollectionCard } from '@/composables/useCollectionCard'
import CollectionModals from '../../collection/CollectionModals.vue'
import DowloadCatalog from '../../collection/DowloadCatalog.vue'
import UserRoleMixin from '../../UserRoleMixin.vue'

export default {
  components: {
    DownloadIconButton,
    BCard,
    BImg,
    BLink,
    BCardBody,
    BButton,
    DowloadCatalog,
    CollectionModals,
    BDropdown,
    BDropdownItem,
    BTooltip,
  },
  mixins: [UserRoleMixin],
  props: {
    collection: {
      type: Object,
      default: () => {},
    },
    isPreviousCollection: {
      type: Boolean,
      default: false,
    },
    fetchCollections: {
      type: Function,
      default() {
      },
    },
  },
  setup(props) {
    const composable = useCollectionCard(props)
    return {
      ...composable,
      ...useBrandCatalogueFlag(),
    }
  },
  data() {
    return {
      selectedCollection: {},
    }
  },
  computed: {
    isCollectionListPage() {
      return this.$route.name === 'collections'
    },
    isErrorInProducts() {
      return this.isBrand && this.isCollectionListPage && this.collection.productsCount === 0
    },
  },
  methods: {
    onImageError(event) {
      event.target.src = this.getDefaultProductImage(300, 300)
    },
    async showCollectionWarningModal(collection) {
      await this.checkRemoveCollection(collection._id, {
        onSuccess: () => this.closeCollectionWarningModal(),
        onNeedsConfirm: () => this.$refs.collectionWarning.show(),
      })
    },
    openDownloadModal(collectionId) {
      this.$bvModal.show(`download_catalog_${collectionId}`)
    },
    showModal(collection) {
      this.$bvModal.show(`updateavailability_${collection._id}`)
      this.showUpdateAvailabilityModal(collection)
    },
    closeCollectionWarningModal() {
      this.$refs.collectionWarning.hide()
    },
    onRemoveCollection() {
      this.removeCollection(this.fetchCollections, {
        onSuccess: () => this.closeCollectionWarningModal(),
      })
    },
    onHandleDownloadCollection() {
      this.handleDownloadCollection(this.collection, this.isErrorInProducts, id => {
        this.$bvModal.show(`download_catalog_${id}`)
      })
    },
    onUpdateCollectionStatus(status) {
      this.updateCollectionStatus(this.collection, status, this.fetchCollections)
    },
    async syncWithShopify(collection) {
      store.dispatch(SYNC_COLLECTION_WITH_SHOPIFY, collection._id)
        .then(res => {
          apiToastSuccess(res.data.message)
        })
        .catch(() => {})
    },
  },
}
</script>

<style lang="scss">
@import '~@core/scss/base/pages/app-brand-preview.scss';
@import '~@core/scss/base/pages/app-collection.scss';

.z-1100 {
  z-index: 1100 !important;
}

// KP-3919: the retailer's entry to the Download Collection popup — a quiet
// indigo icon tile that turns solid on approach; the "Download" tooltip
// arrives after a two-second hover, per spec.
</style>
